import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../config/api';
import { useToast } from '../context/ToastContext';
import styles from '../styles/modules/RatingModal.module.css';

interface RatingModalProps {
    isOpen: boolean;
    onClose: () => void;
    engagement: {
        _id: string;
        jobRole: string;
        date: string;
        place: string;
        facilityName?: string;
        otherParty: {
            _id: string;
            fullName: string;
            userType: string;
        };
    };
    userType: 'staff' | 'employer';
    onRatingSubmitted: () => void;
}

const RatingModal: React.FC<RatingModalProps> = ({
    isOpen,
    onClose,
    engagement,
    userType,
    onRatingSubmitted
}) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [overallRating, setOverallRating] = useState(0);
    
    // Criteria ratings
    const [professionalism, setProfessionalism] = useState(0);
    const [skill, setSkill] = useState(0);
    const [punctuality, setPunctuality] = useState(0);
    const [communication, setCommunication] = useState(0);
    const [payment, setPayment] = useState(0);
    const [jobAccuracy, setJobAccuracy] = useState(0);
    
    const [review, setReview] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Auto-calculate overall rating based on other ratings
    useEffect(() => {
        const ratings: number[] = [];
        
        if (userType === 'employer') {
            // For employer rating staff: professionalism, skill, punctuality, communication
            if (professionalism > 0) ratings.push(professionalism);
            if (skill > 0) ratings.push(skill);
            if (punctuality > 0) ratings.push(punctuality);
            if (communication > 0) ratings.push(communication);
        } else {
            // For staff rating employer: payment, jobAccuracy, communication
            if (payment > 0) ratings.push(payment);
            if (jobAccuracy > 0) ratings.push(jobAccuracy);
            if (communication > 0) ratings.push(communication);
        }

        // Calculate average and round to nearest integer
        if (ratings.length > 0) {
            const average = ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
            setOverallRating(Math.round(average));
        } else {
            setOverallRating(0);
        }
    }, [professionalism, skill, punctuality, communication, payment, jobAccuracy, userType]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (overallRating === 0) {
            setError(t('rating.provideRating'));
            return;
        }

        setSubmitting(true);
        setError('');

        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired'), type: 'error' });
            setSubmitting(false);
            return;
        }

        const ratingData: any = {
            engagementId: engagement._id,
            overallRating,
            communication: communication || undefined,
            review: review.trim() || undefined
        };

        // Add role-specific criteria
        if (userType === 'employer') {
            // Employer rating staff
            if (professionalism) ratingData.professionalism = professionalism;
            if (skill) ratingData.skill = skill;
            if (punctuality) ratingData.punctuality = punctuality;
        } else {
            // Staff rating employer
            if (payment) ratingData.payment = payment;
            if (jobAccuracy) ratingData.jobAccuracy = jobAccuracy;
        }

        try {
            const response = await fetch(getApiUrl('ratings'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(ratingData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || t('rating.submitError'));
            }

            showToast({ message: t('rating.submitSuccess'), type: 'success' });
            onRatingSubmitted();
            onClose();
        } catch (error) {
            console.error('Error submitting rating:', error);
            setError(error instanceof Error ? error.message : 'Failed to submit rating');
        } finally {
            setSubmitting(false);
        }
    };

    const renderStars = (
        value: number,
        setValue: (val: number) => void,
        hovered: number = 0,
        setHovered?: (val: number) => void
    ) => {
        return (
            <div className={styles.starRating}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <span
                        key={star}
                        className={`${styles.star} ${star <= (hovered || value) ? styles.filled : ''}`}
                        onClick={() => setValue(star)}
                        onMouseEnter={() => setHovered && setHovered(star)}
                        onMouseLeave={() => setHovered && setHovered(0)}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.ratingModal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>{t('rating.rateUser', { name: engagement.otherParty.fullName })}</h2>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div className={styles.engagementInfo}>
                    <p><strong>{t(`roles.${engagement.jobRole.toLowerCase().replace(/\s+/g, '').replace('/', '').replace('&', '')}`)}</strong></p>
                    <p>{new Date(engagement.date).toLocaleDateString()}</p>
                    <p>{engagement.facilityName || engagement.place}</p>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Overall Rating - Calculated automatically */}
                    <div className={`${styles.ratingGroup} ${styles.overallRatingDisplay}`}>
                        <label className={styles.ratingLabel}>
                            {t('rating.overallRating')} <span className={styles.autoCalculated}>({t('rating.autoCalculated')})</span>
                        </label>
                        <div className={`${styles.starRating} ${styles.readonly}`}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <span
                                    key={star}
                                    className={`${styles.star} ${star <= overallRating ? styles.filled : ''}`}
                                >
                                    ★
                                </span>
                            ))}
                            {overallRating > 0 && (
                                <span className={styles.ratingValue}> {overallRating}/5</span>
                            )}
                        </div>
                    </div>

                    {/* Role-specific criteria */}
                    {userType === 'employer' ? (
                        <>
                            <div className={styles.ratingGroup}>
                                <label className={styles.ratingLabel}>{t('rating.professionalism')}</label>
                                {renderStars(professionalism, setProfessionalism)}
                            </div>
                            <div className={styles.ratingGroup}>
                                <label className={styles.ratingLabel}>{t('rating.skillLevel')}</label>
                                {renderStars(skill, setSkill)}
                            </div>
                            <div className={styles.ratingGroup}>
                                <label className={styles.ratingLabel}>{t('rating.punctuality')}</label>
                                {renderStars(punctuality, setPunctuality)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.ratingGroup}>
                                <label className={styles.ratingLabel}>{t('rating.paymentPromptness')}</label>
                                {renderStars(payment, setPayment)}
                            </div>
                            <div className={styles.ratingGroup}>
                                <label className={styles.ratingLabel}>{t('rating.jobAccuracy')}</label>
                                {renderStars(jobAccuracy, setJobAccuracy)}
                            </div>
                        </>
                    )}

                    <div className={styles.ratingGroup}>
                        <label className={styles.ratingLabel}>{t('rating.communication')}</label>
                        {renderStars(communication, setCommunication)}
                    </div>

                    {/* Review Text */}
                    <div className={styles.ratingGroup}>
                        <label className={styles.ratingLabel}>{t('rating.review')}</label>
                        <textarea
                            value={review}
                            onChange={(e) => setReview(e.target.value)}
                            maxLength={500}
                            rows={4}
                            placeholder={
                                userType === 'employer'
                                    ? t('rating.reviewPlaceholderStaff')
                                    : t('rating.reviewPlaceholderEmployer')
                            }
                        />
                        <small>{t('rating.charactersCount', { count: review.length })}</small>
                    </div>

                    {error && <div className={styles.errorMessage}>{error}</div>}

                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {t('rating.cancel')}
                        </button>
                        <button
                            type="submit"
                            className={styles.btnPrimary}
                            disabled={submitting || overallRating === 0}
                        >
                            {submitting ? t('rating.submitting') : t('rating.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RatingModal;
